import React, { useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import ComputerIcon from "@mui/icons-material/Computer";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import toast, { Toaster } from "react-hot-toast";
import { api } from "../../../utils/axios-instance";
import { auth } from "../../../helper/firebaseClient";
import { useDepartment } from "../../../stores/departmentStore";
import useAuth from "../../../stores/authStore";

// Gmail (and most providers) cap outbound messages around 25MB total,
// which includes attachment bytes. Enforce the same limit client-side so
// people get instant feedback instead of a failed send.
const MAX_TOTAL_ATTACHMENT_BYTES = 25 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Backend routes for browsing/pulling department files. Both live in the users
// app alongside send_gmail/save-attachment, so the composer only ever talks to
// one prefix. The fetch endpoint returns base64 directly rather than handing
// back a signed GCS URL — that keeps bucket CORS out of the picture.
// ---------------------------------------------------------------------------
const DEPARTMENT_FILES_ENDPOINT = "/users/department-files/";
const DEPARTMENT_FILE_ENDPOINT = (fileId) =>
  `/users/department-file/${fileId}/`;

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileTypeIcon = (mime = "") => {
  const m = mime.toLowerCase();
  if (m.startsWith("image/"))
    return <ImageIcon sx={{ color: "#9757D7", fontSize: 20 }} />;
  if (m === "application/pdf")
    return <PictureAsPdfIcon sx={{ color: "#D93025", fontSize: 20 }} />;
  if (m.startsWith("video/"))
    return <VideoFileIcon sx={{ color: "#374151", fontSize: 20 }} />;
  if (m.startsWith("audio/"))
    return <AudioFileIcon sx={{ color: "#374151", fontSize: 20 }} />;
  return <InsertDriveFileIcon sx={{ color: "#374151", fontSize: 20 }} />;
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result looks like "data:<mime>;base64,<data>"
      const base64 = String(reader.result).split(",")[1] || "";
      resolve({
        id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`,
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        base64,
        source: "device",
      });
    };
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

// ---------------------------------------------------------------------------
// DepartmentFilePicker — lists the files inside one department and lets the
// user pick one to attach. Fetches lazily when opened, so no cost until used.
// ---------------------------------------------------------------------------
const DepartmentFilePicker = ({ department, onClose, onPick, disabledIds }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [pickingId, setPickingId] = useState(null);

  const deptId = department?.id || department?.dept_id || department?._id;
  const deptName = department?.name || department?.dept_name || "Department";

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(DEPARTMENT_FILES_ENDPOINT, {
          params: { department_id: deptId },
        });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.files || [];
        setFiles(list);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to list department files:", err);
        setError("Couldn't load files for this department.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deptId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => (f.name || "").toLowerCase().includes(q));
  }, [files, query]);

  const handlePick = async (file) => {
    setPickingId(file.id);
    try {
      await onPick(file);
    } finally {
      setPickingId(null);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1A1D1F" }}>
            Attach from {deptName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#9A9FA5" }}>
            Pick a file to attach to this email
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search files..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9A9FA5", fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "10px",
              bgcolor: "#F4F4F4",
              "& fieldset": { border: "none" },
            },
          }}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
          {loading && (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && error && (
            <Typography
              sx={{
                py: 4,
                textAlign: "center",
                fontSize: 13,
                color: "#D93025",
              }}
            >
              {error}
            </Typography>
          )}

          {!loading && !error && visible.length === 0 && (
            <Typography
              sx={{
                py: 4,
                textAlign: "center",
                fontSize: 13,
                color: "#9A9FA5",
              }}
            >
              {query ? "No files match that search." : "No files here yet."}
            </Typography>
          )}

          {!loading &&
            !error &&
            visible.map((file, i) => {
              const mime = file.mimeType || file.mimetype || "";
              const size = file.size || 0;
              const alreadyAttached = disabledIds.includes(file.id);
              return (
                <Box
                  key={file.id || i}
                  onClick={() =>
                    !alreadyAttached && !pickingId && handlePick(file)
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderBottom: "1px solid #F3F4F6",
                    borderRadius: "8px",
                    cursor:
                      alreadyAttached || pickingId ? "default" : "pointer",
                    opacity: alreadyAttached ? 0.5 : 1,
                    "&:hover": {
                      bgcolor:
                        alreadyAttached || pickingId
                          ? "transparent"
                          : "#FAFAFA",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", flexShrink: 0 }}>
                    {getFileTypeIcon(mime)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      title={file.name}
                      sx={{ fontSize: 13, fontWeight: 600, color: "#1A1D1F" }}
                    >
                      {file.name || "Untitled"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#9A9FA5" }}>
                      {size ? formatFileSize(size) : ""}
                      {alreadyAttached ? " · already attached" : ""}
                    </Typography>
                  </Box>
                  {pickingId === file.id && <CircularProgress size={16} />}
                </Box>
              );
            })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const InlineCompose = ({ onClose, onDiscard, mailContent, setMailContent }) => {
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [readingFiles, setReadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  // Attach menu state
  const [attachAnchorEl, setAttachAnchorEl] = useState(null);
  const [deptAnchorEl, setDeptAnchorEl] = useState(null);
  const [pickerDepartment, setPickerDepartment] = useState(null);

  // ── Departments the current user is allowed to pull files from ──────────
  const { profileData } = useAuth();
  const {
    departments,
    listDepartments,
    loading: departmentsLoading,
  } = useDepartment();

  const visibleDepartments = useMemo(() => {
    if (!profileData) return [];
    if (
      profileData.role_priv === "org_admin" ||
      profileData.role_priv === "super_admin"
    ) {
      return departments || [];
    }
    const ids = profileData.dept_ids || [];
    const names = profileData.dept_names || [];
    if (ids.length)
      return (departments || []).filter((d) => ids.includes(d.id));
    if (names.length)
      return (departments || []).filter((d) => names.includes(d.name));
    return [];
  }, [profileData, departments]);

  const ensureDepartmentsLoaded = useCallback(() => {
    if (!departments || departments.length === 0) {
      listDepartments();
    }
  }, [departments, listDepartments]);

  const handleChange = (e) => {
    setLoading(false);
    setMailContent({
      ...mailContent,
      [e.target.name]: e.target.value,
    });
  };

  const totalAttachmentBytes = attachments.reduce(
    (sum, a) => sum + (a.size || 0),
    0
  );

  const closeAttachMenus = () => {
    setAttachAnchorEl(null);
    setDeptAnchorEl(null);
  };

  // Single gate for the 25MB cap so device uploads and department pulls can't
  // diverge. Deliberately computed OUTSIDE the state updater: React may invoke
  // an updater more than once (StrictMode/concurrent rendering), which would
  // fire the toast twice and make the return value unreliable.
  const addAttachments = (incoming) => {
    const incomingSize = incoming.reduce((sum, a) => sum + (a.size || 0), 0);
    if (totalAttachmentBytes + incomingSize > MAX_TOTAL_ATTACHMENT_BYTES) {
      toast.error("Attachments can't exceed 25MB in total.");
      return false;
    }
    setAttachments((prev) => [...prev, ...incoming]);
    return true;
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setReadingFiles(true);
    try {
      const read = await Promise.all(files.map(readFileAsBase64));
      addAttachments(read);
    } catch (err) {
      console.error("Failed to read attachment(s):", err);
      toast.error("Failed to attach file(s).");
    } finally {
      setReadingFiles(false);
      // Reset the input so selecting the same file again re-triggers onChange
      e.target.value = "";
    }
  };

  // Pull a department file down through its presigned URL and turn it into the
  // same {filename, mimeType, base64} shape a device upload produces, so the
  // send path stays identical for both sources.
  // Pull a department file down as base64 and normalise it into the exact
  // shape a device upload produces, so send_gmail sees no difference between
  // the two sources.
  const attachDepartmentFile = async (file) => {
    const declaredSize = Number(file.size || 0);

    if (declaredSize && declaredSize > MAX_TOTAL_ATTACHMENT_BYTES) {
      toast.error("That file is larger than the 25MB email limit.");
      return;
    }
    if (
      declaredSize &&
      totalAttachmentBytes + declaredSize > MAX_TOTAL_ATTACHMENT_BYTES
    ) {
      toast.error("Attachments can't exceed 25MB in total.");
      return;
    }

    setReadingFiles(true);
    try {
      const res = await api.get(DEPARTMENT_FILE_ENDPOINT(file.id));
      const { filename, mimeType, size, content } = res.data || {};
      if (!content) throw new Error("No file content returned");

      const added = addAttachments([
        {
          id: `dept-${file.id}-${Date.now()}`,
          fileId: file.id,
          filename: filename || file.name || "attachment",
          mimeType: mimeType || "application/octet-stream",
          size: size || declaredSize,
          base64: content,
          source: "department",
        },
      ]);

      if (added) {
        toast.success(`Attached ${filename || file.name}`);
        closeAttachMenus();
        setPickerDepartment(null);
      }
    } catch (err) {
      console.error("Failed to attach department file:", err);
      toast.error(
        err?.response?.data?.error || "Couldn't attach that file. Try again."
      );
    } finally {
      setReadingFiles(false);
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSubmit = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      setLoading(true);
      const response = await api.post("/users/send_gmail/", {
        to: mailContent.to,
        subject: mailContent.subject,
        content: mailContent.content,
        idToken,
        attachments: attachments.map(({ filename, mimeType, base64 }) => ({
          filename,
          mimeType,
          content: base64,
        })),
      });

      if (response.data.status === "success") {
        toast.success("Email sent successfully.");
        setAttachments([]);
        onDiscard(); // Clears the draft and closes
      } else {
        toast.error("Failed to send email.");
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to send email.");
      setLoading(false);
    }
  };

  const attachedFileIds = attachments
    .filter((a) => a.fileId)
    .map((a) => a.fileId);

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Form */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          pt: 5,
          px: 4,
        }}
      >
        {/* To Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 1.5,
            borderBottom: "1px solid #f5f5f5",
          }}
        >
          <Typography
            sx={{
              color: "#aaa",
              width: "40px",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            To:
          </Typography>
          <TextField
            fullWidth
            name="to"
            placeholder="Recipient's email"
            variant="standard"
            value={mailContent.to}
            onChange={handleChange}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: "15px", color: "#333" },
            }}
          />
        </Box>

        {/* Subject Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 1.5,
            borderBottom: "1px solid #f5f5f5",
          }}
        >
          <TextField
            fullWidth
            name="subject"
            placeholder="Subject"
            variant="standard"
            value={mailContent.subject}
            onChange={handleChange}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: "15px", color: "#333", fontWeight: 500 },
            }}
          />
        </Box>

        {/* Content Area */}
        <Box sx={{ display: "flex", flex: 1, py: 3 }}>
          <TextField
            fullWidth
            multiline
            name="content"
            placeholder="Write your message here..."
            variant="standard"
            value={mailContent.content}
            onChange={handleChange}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: "15px",
                color: "#444",
                alignItems: "flex-start",
                lineHeight: 1.6,
              },
            }}
            sx={{ height: "100%", "& .MuiInputBase-root": { height: "100%" } }}
          />
        </Box>

        {/* Attachments tray */}
        {attachments.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              pb: 2,
            }}
          >
            {attachments.map((a) => (
              <Chip
                key={a.id}
                icon={
                  a.source === "department" ? (
                    <BusinessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <InsertDriveFileIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={`${a.filename} · ${formatFileSize(a.size)}`}
                onDelete={() => removeAttachment(a.id)}
                sx={{
                  bgcolor: "#F4F4F4",
                  fontSize: 12,
                  maxWidth: 260,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          px: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#fff",
          borderTop: "1px solid #fafafa",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "13px", color: "#bbb" }}>
            {auth.currentUser?.email
              ? `Sending as ${auth.currentUser.email}`
              : ""}
          </Typography>
          {attachments.length > 0 && (
            <Typography sx={{ fontSize: "12px", color: "#aaa" }}>
              {attachments.length} file{attachments.length > 1 ? "s" : ""} ·{" "}
              {formatFileSize(totalAttachmentBytes)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFilesSelected}
          />

          <Tooltip title="Attach files">
            <span>
              <IconButton
                onClick={(e) => setAttachAnchorEl(e.currentTarget)}
                disabled={readingFiles}
                sx={{
                  color: "#888",
                  "&:hover": { bgcolor: "#f9f9f9", color: "#555" },
                }}
              >
                {readingFiles ? (
                  <CircularProgress size={18} />
                ) : (
                  <AttachFileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {/* Source picker: device or department */}
          <Menu
            anchorEl={attachAnchorEl}
            open={Boolean(attachAnchorEl)}
            onClose={closeAttachMenus}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <MenuItem
              onClick={() => {
                closeAttachMenus();
                fileInputRef.current?.click();
              }}
            >
              <ListItemIcon>
                <ComputerIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Upload from device</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                ensureDepartmentsLoaded();
                setDeptAnchorEl(e.currentTarget);
              }}
            >
              <ListItemIcon>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Attach from a department</ListItemText>
              <ArrowRightIcon fontSize="small" sx={{ color: "#9A9FA5" }} />
            </MenuItem>
          </Menu>

          {/* Department submenu */}
          <Menu
            anchorEl={deptAnchorEl}
            open={Boolean(deptAnchorEl)}
            onClose={() => setDeptAnchorEl(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {departmentsLoading && (
              <MenuItem disabled sx={{ gap: 1 }}>
                <CircularProgress size={14} /> Loading departments…
              </MenuItem>
            )}
            {!departmentsLoading && visibleDepartments.length === 0 && (
              <MenuItem disabled>No departments found</MenuItem>
            )}
            {!departmentsLoading &&
              visibleDepartments.map((dept) => {
                const deptId = dept.id || dept.dept_id || dept._id;
                const deptName = dept.name || dept.dept_name || deptId;
                return (
                  <MenuItem
                    key={deptId}
                    onClick={() => {
                      setPickerDepartment(dept);
                      closeAttachMenus();
                    }}
                  >
                    {deptName}
                  </MenuItem>
                );
              })}
          </Menu>

          <Button
            onClick={onDiscard}
            variant="text"
            sx={{
              color: "#888",
              textTransform: "none",
              fontWeight: 500,
              px: 2,
              "&:hover": { bgcolor: "#f9f9f9", color: "#555" },
            }}
          >
            Discard
          </Button>
          <Button
            disabled={loading}
            onClick={handleSubmit}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: "#1a1a1a",
              color: "#fff",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 0.8,
              borderRadius: "6px",
              "&:hover": { bgcolor: "#333" },
            }}
          >
            Send Email
            {loading && (
              <CircularProgress size={16} color="inherit" sx={{ ml: 1.5 }} />
            )}
          </Button>
        </Box>
      </Box>

      {/* Department file browser */}
      {pickerDepartment && (
        <DepartmentFilePicker
          department={pickerDepartment}
          onClose={() => setPickerDepartment(null)}
          onPick={attachDepartmentFile}
          disabledIds={attachedFileIds}
        />
      )}
    </Box>
  );
};

export default InlineCompose;
